// Web mock for @react-native-community/datetimepicker
import React, { useEffect, useRef } from 'react';

function flattenStyle(style) {
  if (Array.isArray(style)) {
    return Object.assign({}, ...style.filter(Boolean));
  }
  return style || {};
}

// Read `<input type="date">` back in LOCAL time. Its value is "YYYY-MM-DD",
// which `new Date(string)` parses as UTC midnight — so in any negative-offset
// timezone a caller using local getters (e.g. `toDateKey`) reads back the
// PREVIOUS day, and the value-sync `useEffect` below then snaps the input back
// a day. Build local midnight for that shape instead, mirroring `parseDateKey`
// in utils/residents/todoCalendar. A `time` value ('HH:MM', or 'HH:MM:SS' with a
// seconds step) has no day at all — `new Date('14:30')` is an Invalid Date, so
// every pick used to be dropped — so it is put on the current value's day, in
// local time. `datetime-local` values are already interpreted as local time, so
// only they keep the plain `new Date(...)` path.
const DATE_ONLY_VALUE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_ONLY_VALUE = /^\d{2}:\d{2}(:\d{2})?$/;
const parseInputValue = (inputValue, current) => {
  if (DATE_ONLY_VALUE.test(inputValue)) {
    const [y, m, d] = inputValue.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  if (TIME_ONLY_VALUE.test(inputValue)) {
    const [h, mi] = inputValue.split(':').map(Number);
    const day = current instanceof Date && !isNaN(current.getTime()) ? new Date(current.getTime()) : new Date();
    day.setHours(h, mi, 0, 0);
    return day;
  }
  return new Date(inputValue);
};

const DateTimePicker = ({
  value,
  mode = 'date',
  onChange,
  minimumDate,
  maximumDate,
  style,
  display,
  is24Hour,
  locale,
  timeZoneOffsetInMinutes,
  timeZoneName,
  positiveButton,
  negativeButton,
  neutralButton,
  themeVariant,
  accentColor,
  textColor,
  minuteInterval,
  onError,
  accessibilityLabel,
  ...props
}) => {
  const inputRef = useRef(null);
  const inputType = mode === 'time' ? 'time' : mode === 'datetime' ? 'datetime-local' : 'date';
  // Format dates in LOCAL time — `<input type="date|datetime-local|time">`
  // interprets its `value`/`min`/`max` as local time. Using `.toISOString()`
  // (UTC) would produce strings that never match the DOM input's value,
  // making the value-sync `useEffect` below fire on every render and clobber
  // the user's in-progress digit entry.
  const pad = (n) => String(n).padStart(2, '0');
  const formatForInput = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    if (inputType === 'date') return `${yyyy}-${mm}-${dd}`;
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    if (inputType === 'time') return `${hh}:${mi}`;
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };
  const externalValue = formatForInput(value);

  // Keep the input uncontrolled and only push external value changes back to
  // the DOM when they differ. `<input type="date|datetime-local|time">` fires
  // onChange on every digit; controlling `value` clobbers the browser's
  // in-progress multi-digit entry (typing "12" for month ends up as "02"
  // because the first digit re-renders the value before the second arrives).
  useEffect(() => {
    const el = inputRef.current;
    if (el && el.value !== externalValue) {
      el.value = externalValue;
    }
  }, [externalValue]);

  const handleChange = (event) => {
    const date = parseInputValue(event.target.value, value);
    if (!isNaN(date.getTime()) && onChange) {
      onChange({ type: 'set', nativeEvent: { timestamp: date.getTime() } }, date);
    }
  };

  // A cleared or half-typed input reads '' — the browser only reports whole
  // values — which handleChange drops, so the caller keeps its value. Put that
  // value back when the input is left, or it reads blank while the form holds
  // (and saves) something else. Unconditional: half-typed segments read '' too,
  // so against an empty value a comparison would leave them on screen.
  const handleBlur = (event) => {
    event.target.value = externalValue;
  };

  return React.createElement('input', {
    ref: inputRef,
    type: inputType,
    defaultValue: externalValue,
    min: formatForInput(minimumDate) || undefined,
    max: formatForInput(maximumDate) || undefined,
    onChange: handleChange,
    onBlur: handleBlur,
    // Callers name the picker the React Native way; a raw DOM node needs aria-label.
    'aria-label': accessibilityLabel,
    style: { padding: '8px', border: '1px solid #ccc', borderRadius: '4px', ...flattenStyle(style) },
    ...props,
  });
};

export default DateTimePicker;
